<script setup>
import ConvertApikomTaskIdeaAction from "@frontJs/components/actions/projectManagement/tasks/ConvertApikomTaskIdeaAction.vue";
import SaveSubTaskAction from "@frontJs/components/actions/projectManagement/tasks/SaveSubTaskAction.vue";
import SaveTaskAction from "@frontJs/components/actions/projectManagement/tasks/SaveTaskAction.vue";
import SaveTaskTimeAction from "@frontJs/components/actions/projectManagement/tasks/SaveTaskTimeAction.vue";
import ToggleTaskStatusAction from "@frontJs/components/actions/projectManagement/tasks/ToggleTaskStatusAction.vue";
import {
  PencilIcon,
  ClockIcon,
  XMarkIcon,
  PlusIcon,
  ChevronDownIcon,
  CheckIcon,
  ArrowPathIcon,
} from "@heroicons/vue/24/outline";
import ActionButton from "@js/components/ActionButton.vue";
import { useRoute } from "@js/utils/route";
import { toast } from "vue3-toastify";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const {
  row,
  callback,
  idea = false,
} = defineProps({
  row: {
    type: Object,
    required: true,
  },
  callback: {
    type: Function,
    required: true,
  },
  idea: {
    type: Boolean,
    required: false,
  },
});

const { route } = useRoute();

function saveActionCallback() {
  toast.success(t("toast.successfullySaved"), { position: "top-right" });
  callback();
}
</script>

<template>
  <div class="flex items-center justify-start gap-2 p-4">
    <template v-if="idea && row.original.isIdea">
      <ConvertApikomTaskIdeaAction
        :task="row.original"
        :callback="saveActionCallback"
      >
        <template #button="{ action }">
          <ActionButton
            size="small"
            type="secondary"
            class="size-8"
            :callback="() => action()"
            :title="t('button.convertToTask')"
            :icon="ArrowPathIcon"
          />
        </template>
      </ConvertApikomTaskIdeaAction>
    </template>

    <ToggleTaskStatusAction
      v-if="
        (row.original.status === 'open' ||
          row.original.status === 'to_process') &&
        !row.original.isIdea
      "
      :task="row.original"
      :action="row.original.status === 'to_process' ? 'process' : 'close'"
      :callback="saveActionCallback"
    >
      <template #button="{ action }">
        <ActionButton
          size="small"
          type="secondary"
          class="size-8"
          :callback="() => action()"
          :title="
            row.original.status === 'to_process'
              ? t('button.processTask')
              : t('button.closeTask')
          "
          :icon="row.original.status === 'to_process' ? CheckIcon : XMarkIcon"
        />
      </template>
    </ToggleTaskStatusAction>

    <SaveTaskTimeAction
      v-if="
        (row.original.status === 'open' ||
          row.original.status === 'to_process') &&
        !row.original.isIdea
      "
      :task="row.original"
      :callback="saveActionCallback"
    >
      <template #button="{ setIsOpen }">
        <ActionButton
          size="small"
          type="secondary"
          class="size-8"
          :callback="() => setIsOpen(true)"
          :title="t('button.addTime')"
          :icon="ClockIcon"
        />
      </template>
    </SaveTaskTimeAction>

    <ActionButton
      size="small"
      :href="
        row.original.isIdea
          ? route('front.projectManagement.task.updateTaskIdea', {
              task: row.original.id,
            })
          : route('front.projectManagement.task.updateTask', {
              task: row.original.id,
            })
      "
      :title="t('button.edit')"
      class="size-8"
      :icon="PencilIcon"
    />

    <SaveTaskAction
      v-if="!row.original.isChild"
      :task="row.original"
      :callback="saveActionCallback"
    >
      <template #button="{ setIsOpen }">
        <ActionButton
          size="small"
          type="secondary"
          class="size-8"
          :callback="() => setIsOpen(true)"
          :title="t('button.edit')"
          :icon="PencilIcon"
        />
      </template>
    </SaveTaskAction>

    <SaveSubTaskAction
      v-if="
        row.original.status === 'open' || row.original.status === 'to_process'
      "
      :task="!row.original.isChild ? row.original : row.original.parent"
      :sub-task="row.original.isChild ? row.original : null"
      :callback="saveActionCallback"
    >
      <template #button="{ setIsOpen }">
        <ActionButton
          size="small"
          type="secondary"
          class="size-8"
          :callback="() => setIsOpen(true)"
          :title="!row.original.isChild ? t('button.add') : t('button.edit')"
          :icon="!row.original.isChild ? PlusIcon : PencilIcon"
        />
      </template>
    </SaveSubTaskAction>

    <ActionButton
      v-if="row.getCanExpand()"
      size="small"
      type="secondary"
      class="size-8"
      :title="row.getIsExpanded() ? t('button.collapse') : t('button.expand')"
      :callback="() => row.toggleExpanded()"
    >
      <ChevronDownIcon
        class="size-5 flex-shrink-0 duration-150"
        :class="{
          'rotate-180 transform': row.getIsExpanded(),
        }"
      />
    </ActionButton>
  </div>
</template>
